import hash from "stable-hash";
import { Cleanroom } from "../types/cleanroom";
import { OrganizationConfig } from "../types/config";
import { ACTIVATION_CONNECTOR_ID } from "../../../common/utils/constants";
import {
    Activation,
    SelectOptionsWithCategory,
} from "../../pages/CleanroomCreateInstall/cleanroomCreateInstall.types";
import CleanroomManagerV2 from "../managers/CleanroomManager/CleanroomManager";
import {
    SNOWFLAKE_QUOTED_ID_PATTERN,
    SNOWFLAKE_UNQUOTED_ID_PATTERN,
} from "../constants/constants";

/**
 * A utility type that recursively generates a union of all possible
 * nested keys of an object type `T`. The keys are represented as
 * dot-separated strings.
 *
 * @template T - The object type to extract nested keys from.
 *
 * @remarks
 * - If `T` is an array, the keys will include numeric indices.
 * - If a property of `T` is an array, the keys will include the array
 *   indices in square brackets.
 * - If a property of `T` is an object, the keys will include the nested
 *   object keys, concatenated with a dot.
 * - If `T` is a union type, the keys will be resolved for each type in
 *   the union.
 *
 */
export type DotConcat<T extends string, U extends string> = `${T}.${U}`;
export type NestedKeys<
    T,
    Depth extends number = 5,
    CurrentDepth extends number = 0,
> = T extends object
    ? CurrentDepth extends Depth // If we hit the depth limit, stop recursion
        ? never
        : T extends any[] // If the type is an array
          ?
                | `${number}`
                | DotConcat<
                      `${number}`,
                      NestedKeys<T[number], Depth, CurrentDepth | 1>
                  >
          : {
                [K in keyof T]: T[K] extends Array<infer U> // If property is an array
                    ?
                          | `${K & string}`
                          | DotConcat<
                                `${K & string}[${number}]`,
                                NestedKeys<U, Depth, CurrentDepth | 1>
                            >
                    : T[K] extends object // If property is an object
                      ?
                            | `${K & string}`
                            | DotConcat<
                                  `${K & string}`,
                                  NestedKeys<T[K], Depth, CurrentDepth | 1>
                              >
                      : `${K & string}`;
            }[keyof T]
    : never;

export const get_locator_region_csp_from_identifier = (identifier) => {
    const parts = identifier?.split(".") || [];

    if (parts.length < 3) {
        return {};
    }

    const locator = parts.shift();
    const csp = parts.pop();
    const region = parts.join(".");

    return {
        locator: locator,
        region: region,
        csp: csp,
    };
};

export const isSameRegionAndCloud = ({
    identifier = "",
    accountIdentifier = "",
}) => {
    const { region = "", csp = "" } =
        get_locator_region_csp_from_identifier(identifier);
    const { region: account_region = "", csp: account_csp = "" } =
        get_locator_region_csp_from_identifier(accountIdentifier);
    return (
        region.toUpperCase() === account_region.toUpperCase() &&
        csp.toUpperCase() === account_csp.toUpperCase()
    );
};

export const isObjectEmpty = (objectName) => {
    if (objectName === null || objectName === undefined) return true;
    for (let prop in objectName) {
        if (objectName.hasOwnProperty(prop)) {
            return false;
        }
    }
    return true;
};

export const parseFQTNC = (fqtnc) => {
    const fqtncLength = fqtnc.length;
    if (fqtncLength === 0) return [];

    const returnArray = [];
    let inDoubleQuotes = false;
    let i = 0;
    let currentWordStartIndex = 0;

    while (i < fqtncLength) {
        const currentCharacter = fqtnc[i];
        if (currentCharacter === '"') {
            if (inDoubleQuotes) {
                if (i < fqtncLength - 1 && fqtnc[i + 1] === '"') {
                    i += 1;
                } else {
                    inDoubleQuotes = false;
                }
            } else {
                inDoubleQuotes = true;
            }
        } else if (currentCharacter === "." && !inDoubleQuotes) {
            returnArray.push(fqtnc.substring(currentWordStartIndex, i));
            currentWordStartIndex = i + 1;
        }
        i += 1;
    }

    returnArray.push(fqtnc.substring(currentWordStartIndex, i));
    return returnArray;
};

export const addPrefixToObjKeys = (
    obj: { [key: string]: string },
    prefix: string,
) => {
    const newObj: { [key: string]: string } = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            newObj[`${prefix}${key}`] = obj[key];
        }
    }
    return newObj;
};

export const buildColumnOptionsFromFQTN = (
    fqtn: string[] = [],
): SelectOptionsWithCategory[] => {
    const outputTables: SelectOptionsWithCategory[] = [];
    fqtn.forEach((table) => {
        const [DB, SCHEMA, TABLE] = parseFQTNC(table);
        // const [DB, SCHEMA, TABLE] = table.split(".");
        const categoryLabel = `${DB}.${SCHEMA}`;
        const outputTablefind = outputTables.find(
            (t) => t.label === categoryLabel,
        );
        if (!outputTablefind) {
            outputTables.push({
                label: categoryLabel,
                options: [{ label: TABLE, value: table }],
            });
        } else {
            outputTablefind.options.push({ label: TABLE, value: table });
        }
    });
    return outputTables;
};

export const buildColumnOptionsFromFQTC = (
    fqtc: string[],
): SelectOptionsWithCategory[] => {
    const tables = [
        ...new Set(
            fqtc.map((col) => {
                const parsed = parseFQTNC(col);
                return parsed.splice(1, 2).join(".");
            }),
        ),
    ];
    return tables.map((table) => ({
        label: table,
        options: fqtc
            .filter((column) => {
                const parsed = parseFQTNC(column);
                return parsed.splice(1, 2).join(".") === table;
            })
            .map((col) => {
                return { label: col, value: col };
            }),
    }));
};
export const FQTCToFQTN = (fqtc: string): string =>
    parseFQTNC(fqtc).slice(0, -1).join(".");

export const convertToTitleCase = (
    array: string[] = [],
    separator: string = "_",
) => {
    return array.map((words) =>
        words
            .split(separator)
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" "),
    );
};

/**
 * Replaces the provider or collaborator name in the activation list based on the user's role and selected collaborator.
 *
 * @param {Activation[]} activations - The list of activations to be processed.
 * @param {Cleanroom} cleanroom - The cleanroom object containing details about the cleanroom.
 * @param {OrganizationConfig} orgConfig - The organization configuration containing user and account details.
 * @param {string} [selectedCollaborator] - The identifier of the selected collaborator (optional).
 * @returns {Activation[]} - The updated list of activations with replaced names and icons.
 */
export const replaceProviderCollaboratorNameFromActivationList = (
    activations: Activation[],
    cleanroom: Cleanroom,
    orgConfig: OrganizationConfig,
    selectedCollaborator?: string,
): Activation[] => {
    const { account: orgAccount } = orgConfig;
    const isCleanroomProvider =
        CleanroomManagerV2.isCleanroomProvider({
            cleanroom,
        }) || !cleanroom.creator?.account;

    return activations.map((activation) => {
        const { id } = activation;

        if (id === ACTIVATION_CONNECTOR_ID.EXPORT_TO_PROVIDER) {
            return {
                ...activation,
                name: isCleanroomProvider
                    ? orgAccount.name
                    : cleanroom.creator.account.name,
                icon: isCleanroomProvider
                    ? activation.icon
                    : cleanroom.creator.account.logo,
            };
        }

        if (id === ACTIVATION_CONNECTOR_ID.EXPORT_TO_CONSUMER) {
            const collaboratorObj = cleanroom?.collaborators?.find(
                (collaborator) =>
                    collaborator.account.identifier_lrc ===
                    selectedCollaborator,
            );
            return {
                ...activation,
                name: isCleanroomProvider
                    ? (selectedCollaborator &&
                          collaboratorObj?.account.alias) ||
                      "Collaborator"
                    : cleanroom.collaborators?.[0].account.name,
                icon: isCleanroomProvider
                    ? activation.icon
                    : cleanroom.collaborators?.[0].account.logo,
            };
        }

        return activation;
    });
};

export const generateRequestId = async (data) => {
    const weakHash = hash(data);
    const encodedWeakHash = encodeURIComponent(weakHash);
    const buffer = new TextEncoder().encode(encodedWeakHash);
    return btoa(
        Array.from(buffer, (byte) => String.fromCodePoint(byte)).join(""),
    );
};

function base64ToBytes(base64) {
    const binString = atob(base64);
    return Uint8Array.from(binString, (m) => m.codePointAt(0));
}

export const formatMillisecondsToReadableTime = (
    milliseconds: number,
): string => {
    if (milliseconds === null || milliseconds === undefined) return "";
    // Convert to seconds first
    let seconds = Math.round(milliseconds / 1000);

    // Calculate hours, minutes, and remaining seconds
    const hours = Math.floor(seconds / 3600);
    seconds = seconds % 3600;

    const minutes = Math.floor(seconds / 60);
    seconds = seconds % 60;

    const parts = [];

    if (hours > 0) {
        parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
    }
    if (minutes > 0) {
        parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
    }
    if (seconds > 0 || parts.length === 0) {
        parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
    }

    return parts.join(" ");
};

export const checkForUnQuoted = (value: string) =>
    SNOWFLAKE_UNQUOTED_ID_PATTERN.test(value);

export const checkForQuoted = (value: string) =>
    SNOWFLAKE_QUOTED_ID_PATTERN.test(value);
